import React, { useMemo } from 'react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';
import { CHART_COLORS, formatDateLabel, isDateValue, inferFormat } from './chartUtils';
import { ChartTooltip } from './ChartTooltip';

/**
 * Componente de bar chart compartido.
 *
 * Props:
 *  - chartData: { labels: string[], series: Array<{name, label, period, data, formattedData}> }
 *  - height: number (default 300)
 *  - colors: string[] (default CHART_COLORS)
 *  - showTooltip: boolean (default true)
 *  - showLegend: boolean (default true)
 */
export function SharedBarChart({
    chartData,
    height = 300,
    colors = CHART_COLORS,
    showTooltip = true,
    showLegend = true,
}) {
    const { labels, series } = chartData;

    // Transformar a formato Recharts
    const data = useMemo(() => {
        return labels.map((label, i) => {
            const point = { label };
            series.forEach((serie, si) => {
                const key = `series${si}`;
                point[key] = serie.data[i];
                point[`${key}_label`] = serie.label;
                if (serie.formattedData && serie.formattedData[i] != null) {
                    point[`${key}_formatted`] = serie.formattedData[i];
                }
            });
            return point;
        });
    }, [labels, series]);

    // Mapa de series para el tooltip
    const seriesMap = useMemo(() => {
        const map = {};
        series.forEach((serie, i) => {
            map[`series${i}`] = { name: serie.name, label: serie.label, period: serie.period };
        });
        return map;
    }, [series]);

    // Formato de cada serie para Y axis ticks
    const seriesFormats = useMemo(() =>
        series.map(s => inferFormat(s.name)),
    [series]);

    // Dual Y axis: cuando hay 2+ métricas con escalas muy diferentes (ratio > 10x)
    const hasDualAxis = useMemo(() => {
        if (series.length < 2) return false;
        const maxValues = series.map(s => {
            const nums = s.data.filter(v => v != null && v !== 0);
            return nums.length > 0 ? Math.max(...nums.map(Math.abs)) : 0;
        });
        const nonZero = maxValues.filter(v => v > 0);
        if (nonZero.length < 2) return false;
        const maxAll = Math.max(...nonZero);
        const minAll = Math.min(...nonZero);
        return (maxAll / minAll) > 10;
    }, [series]);

    // Asignar cada serie al eje izquierdo o derecho por magnitud
    const axisAssignment = useMemo(() => {
        if (!hasDualAxis || series.length < 2) return null;
        const seriesMaxVals = series.map(s => {
            const nums = s.data.filter(v => v != null && v !== 0);
            return nums.length > 0 ? Math.max(...nums.map(Math.abs)) : 0;
        });
        const sorted = [...seriesMaxVals].sort((a, b) => b - a);
        const threshold = sorted[Math.floor(sorted.length / 2)];
        return seriesMaxVals.map(v => v >= threshold ? 'left' : 'right');
    }, [hasDualAxis, series]);

    // Formato dominante de cada eje para los ticks
    const axisFormats = useMemo(() => {
        if (!hasDualAxis || !axisAssignment) return null;
        const leftIdx = axisAssignment.indexOf('left');
        const rightIdx = axisAssignment.indexOf('right');
        return {
            left: leftIdx >= 0 ? seriesFormats[leftIdx] : seriesFormats[0],
            right: rightIdx >= 0 ? seriesFormats[rightIdx] : seriesFormats[seriesFormats.length - 1],
        };
    }, [hasDualAxis, axisAssignment, seriesFormats]);

    const labelsAreDates = labels.length > 0 && isDateValue(labels[0]);

    if (series.length === 0 || labels.length === 0) return null;

    const yTickFormatter = (format) => (value) => {
        if (format === 'currency') {
            if (Math.abs(value) >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
            if (Math.abs(value) >= 1000) return `$${(value / 1000).toFixed(0)}K`;
            return `$${value}`;
        }
        if (format === 'percentage') return `${value}%`;
        if (Math.abs(value) >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
        if (Math.abs(value) >= 1000) return `${(value / 1000).toFixed(0)}K`;
        return new Intl.NumberFormat('es-AR').format(value);
    };

    return (
        <ResponsiveContainer width="100%" height={height}>
            <BarChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                    dataKey="label"
                    tickFormatter={labelsAreDates ? formatDateLabel : undefined}
                />
                {hasDualAxis && axisFormats ? (
                    <>
                        <YAxis yAxisId="left" orientation="left" tickFormatter={yTickFormatter(axisFormats.left)} />
                        <YAxis yAxisId="right" orientation="right" tickFormatter={yTickFormatter(axisFormats.right)} />
                    </>
                ) : (
                    <YAxis tickFormatter={seriesFormats.length > 0 ? yTickFormatter(seriesFormats[0]) : undefined} />
                )}
                {showTooltip && <Tooltip content={<ChartTooltip seriesMap={seriesMap} />} />}
                {showLegend && <Legend />}
                {series.map((serie, index) => (
                    <Bar
                        key={serie.name}
                        dataKey={`series${index}`}
                        name={serie.label}
                        fill={colors[index % colors.length]}
                        {...(hasDualAxis && axisAssignment
                            ? { yAxisId: axisAssignment[index] }
                            : {})}
                    />
                ))}
            </BarChart>
        </ResponsiveContainer>
    );
}
